import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  makeRenderOption,
  usePreviewTableMaterialIdInputStyles,
} from "../../../BulkATCPopup/components/PreviewTable/PreviewTableMaterialIdInput";
import { fetchProductSuggestionsForPreview } from "../../../BulkATCPopup/api/getPreviewProductSuggestions";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { useOptions } from "../../../common/components/Select/SimpleAutocompleteSelect";
import { AUTOSUGGEST_DEBOUNCE_TIME } from "../../../utils/constants";
import { type SrpProductData } from "../../../utils/models";
import debounce from "lodash/debounce";
import { Autocomplete, type AutocompleteProps } from "../../../common/components/Select/AutocompleteSelect";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { isNonNull } from "~/utils/reducers";

export interface AutosuggestProductInputProps extends Omit<AutocompleteProps<string, false, false, true>, "options"> {
  label?: ReactNode;
  autoFocus?: boolean;
  products: SrpProductData[];
  disableNewVerLink?: boolean;
}

export const AutosuggestProductInput = ({
  autoFocus,
  classes: outerClasses,
  inputValue,
  products,
  disableNewVerLink,
  ...rest
}: AutosuggestProductInputProps) => {
  const { t } = useTranslation();
  const { options, optionMap } = useOptions(products, (item) => item.designation);

  const { option: optionCls, ...internalClasses } = usePreviewTableMaterialIdInputStyles();

  const classes = {
    ...(outerClasses || {}),
    option: classNames(optionCls),
    paper: classNames(internalClasses.paper, outerClasses?.paper),
    listbox: classNames(internalClasses.listbox, outerClasses?.listbox),
  };

  return (
    <Autocomplete
      classes={classes}
      value={inputValue}
      data-test-id={"autocomplete-designation"}
      inputValue={inputValue}
      blurOnSelect={true}
      isOptionEqualToValue={(option, value) => value.toLowerCase() === option.toLowerCase()}
      options={options}
      freeSolo={true}
      loadingText={t("spinner.text")}
      // TODO - Check after m-ui update
      disableClearable={true as false}
      autoFocus={autoFocus}
      filterOptions={(option: string[]) => option} // just pass through all the options --- assume proper filtering is done in BE
      renderOption={makeRenderOption(optionMap, optionCls, t, disableNewVerLink)}
      {...rest}
    />
  );
};

type FetchSuggestionsFunc = (query: string, abortSignal: AbortSignal) => Promise<SrpProductData[]>;

export const makeUseFetchProductAutosuggest = (fetchFunc: FetchSuggestionsFunc) => {
  const productsMap: Dict<SrpProductData> = {};

  return (inputValue: string) => {
    const { val: isOpen, setTrue: open, setFalse: close } = useBooleanState(false);
    const [loading, setLoading] = useState(false);
    const [suggestionCodes, setSuggestionCodes] = useState<string[]>([]);

    const fetchData = useMemo(
      () =>
        debounce(async (val: string, abortSignal: AbortSignal) => {
          setLoading(true);
          const data = await fetchFunc(val, abortSignal);

          const products = data.filter((item) => item.designation !== undefined);
          // added sorting
          products.sort((a, b) => a.designation.length - b.designation.length);

          products.forEach((product) => {
            if (!productsMap[product.code]) {
              productsMap[product.code] = product;
            }
          });

          setSuggestionCodes(products.map((product) => product.code));

          setLoading(false);
        }, AUTOSUGGEST_DEBOUNCE_TIME),
      [],
    );

    useEffect(() => {
      if (isOpen && inputValue && inputValue.length >= 3) {
        const controller = new AbortController();
        fetchData(inputValue, controller.signal);
        return () => controller.abort();
      }
    }, [inputValue, isOpen]);

    const suggestions = useMemo(() => suggestionCodes.map((code) => productsMap[code]), [suggestionCodes]);

    const selectedProduct = findSelectedProduct(inputValue, suggestions);

    return {
      inputProps: {
        loading,
        open: isOpen,
        onOpen: open,
        onClose: close,
      },
      products: suggestions,
      selectedProduct,
      packCode: selectedProduct ? getPackCode(inputValue) : "",
    };
  };
};

export const useFetchProductAutosuggest = makeUseFetchProductAutosuggest(
  async (query: string, abortSignal: AbortSignal) => {
    const res = await fetchProductSuggestionsForPreview(query, abortSignal);
    return res.data;
  },
);

const findSelectedProduct = (inputValue: string, products: SrpProductData[]) => {
  const rawVal = inputValue.toLowerCase().trim();
  const filteredMatNo = rawVal.replace(/-/g, "");
  const matNo = filteredMatNo.length === 15 ? filteredMatNo.substring(0, 13) : "";

  // We must search both suggested products and their successors, since SCHAEFF-14754 can cause to switch to successor product
  return products
    .flatMap((product) => [product, product.successorProduct])
    .filter(isNonNull)
    .find((product) => product.designation.toLowerCase() === rawVal || product.sapMaterialNumber === matNo) as
    | SrpProductData
    | undefined;
};

const getPackCode = (inputValue: string) => {
  const packCode = inputValue.length === 15 ? inputValue.substring(13) : "";

  return packCode;
};

export const isSameProduct = (inputValue: string, product: SrpProductData | undefined) => {
  const rawVal = inputValue.toLowerCase().trim();
  const filteredMatNo = rawVal.replace(/-/g, "");
  const matNo = filteredMatNo.length === 15 ? filteredMatNo.substring(0, 13) : "";

  return (
    !!product && (product.designation.toLowerCase() === rawVal || (!!matNo && product.sapMaterialNumber === matNo))
  );
};
